<template>
  <div class="robot-detail container" v-loading="loading">
    <WelcomeBanner :title="robot.robot_name || '机器人详情'" :subtitle="robot.robot_desc || ''">
      <div slot="right">
        <el-button type="text" icon="el-icon-arrow-left" size="small" class="back-button" @click="goBack">返回</el-button>
        <el-button type="primary" icon="el-icon-edit" size="small" @click="handleEdit">编辑</el-button>
        <el-button icon="el-icon-refresh" size="small" @click="loadData">刷新</el-button>
      </div>
    </WelcomeBanner>

    <BaseCard>
      <el-row :gutter="20">
      <!-- 左侧：基本信息卡片 -->
      <el-col :span="8">
        <el-card class="info-card">
          <div class="avatar-wrapper">
            <div class="avatar-container" @click="showFullscreenAvatar">
              <img v-if="avatarBase64" :src="avatarBase64" class="avatar" />
              <div v-else class="avatar-placeholder" :style="{ background: getAvatarGradient(robot.robot_name) }">
                {{ robot.robot_name ? robot.robot_name.charAt(0) : '?' }}
              </div>
              <div class="avatar-overlay">
                <i class="el-icon-full-screen"></i>
              </div>
            </div>
            <h2>{{ robot.robot_name }}</h2>
          </div>
          <div class="info-item">
            <span class="label">账号：</span>
            <span>{{ robot.robot_account }}</span>
          </div>
          <div class="info-item">
            <span class="label">角色：</span>
            <span>{{ robot.robot_role }}</span>
          </div>
          <div class="info-item">
            <span class="label">描述：</span>
            <span>{{ robot.robot_desc }}</span>
          </div>
          <div class="info-item" v-if="robot.robot_video">
            <span class="label">数字人视频：</span>
            <span>{{ robot.robot_video }}</span>
          </div>
          <div class="status-wrapper">
            <span class="label">当前状态：</span>
            <span :class="['status-badge', getStatusClass(robot.realtime_status)]">{{ getStatusText(robot.realtime_status) }}</span>
          </div>
        </el-card>
        
        <el-card class="stats-card" style="margin-top: 20px;">
          <h3>统计数据</h3>
          <el-row :gutter="10">
            <el-col :span="12">
              <div class="stat-item">
                <div class="stat-value">{{ stats.total_tasks }}</div>
                <div class="stat-label">累计完成任务</div>
              </div>
            </el-col>
            <el-col :span="12">
              <div class="stat-item">
                <div class="stat-value">{{ stats.pending_tasks }}</div>
                <div class="stat-label">待处理任务</div>
              </div>
            </el-col>
          </el-row>
          <el-row :gutter="10">
            <el-col :span="12">
              <div class="stat-item">
                <div class="stat-value">{{ stats.processing_tasks }}</div>
                <div class="stat-label">处理中任务</div>
              </div>
            </el-col>
            <el-col :span="12">
              <div class="stat-item">
                <div class="stat-value">{{ stats.completed_tasks }}</div>
                <div class="stat-label">已完成任务</div>
              </div>
            </el-col>
          </el-row>
          <el-row :gutter="10">
            <el-col :span="12">
              <div class="stat-item">
                <div class="stat-value">{{ stats.failed_tasks }}</div>
                <div class="stat-label">失败任务</div>
              </div>
            </el-col>
            <el-col :span="12">
              <div class="stat-item">
                <div class="stat-value">{{ stats.waiting_dependency_tasks }}</div>
                <div class="stat-label">等待依赖</div>
              </div>
            </el-col>
          </el-row>
        </el-card>
      </el-col>
      
      <!-- 右侧：最近任务、会话、工作记录和汇报 -->
      <el-col :span="16">
        <!-- 最近任务 -->
        <el-card>
          <h3>最近任务</h3>
          <div v-if="!recentTasks.length" class="empty-text">暂无任务</div>
          <div v-else class="list-container">
            <div v-for="task in recentTasks" :key="task.task_id" class="task-item" @click="goToTask(task.task_id)">
              <div class="task-header">
                <span class="task-title">{{ task.task_title || task.task_name || '未命名任务' }}</span>
                <el-tag size="mini" :type="getTaskStatusType(task.status)">{{ getTaskStatusText(task.status) }}</el-tag>
              </div>
              <div class="task-meta">
                <span class="task-time">{{ formatTime(task.update_time) }}</span>
                <span v-if="task.priority" class="task-priority" :class="'priority-' + task.priority">优先级: {{ task.priority }}</span>
              </div>
            </div>
          </div>
        </el-card>
        
        <!-- 最近会话 -->
        <el-card style="margin-top: 20px;">
          <h3>最近会话</h3>
          <div v-if="!recentSessions.length" class="empty-text">暂无会话</div>
          <div v-else class="list-container">
            <div v-for="session in recentSessions" :key="session.session_id" class="session-item">
              <div class="session-header">
                <span class="session-title">会话 #{{ session.session_id.slice(-8) }}</span>
                <el-tag size="mini" :type="getSessionStatusType(session.status)">{{ getSessionStatusText(session.status) }}</el-tag>
              </div>
              <div class="session-meta">
                <span class="session-time">{{ formatTime(session.update_time) }}</span>
                <span v-if="session.msg_count" class="session-count">消息数: {{ session.msg_count }}</span>
              </div>
            </div>
          </div>
        </el-card>
        
        <!-- 最近工作记录 -->
        <el-card style="margin-top: 20px;">
          <h3>最近工作记录</h3>
          <div v-if="!recentLogs.length" class="empty-text">暂无记录</div>
          <div v-else class="list-container">
            <div v-for="log in recentLogs" :key="log.log_id" class="log-item">
              <div class="log-header">
                <span class="log-time">{{ formatTime(log.create_time) }}</span>
                <el-tag size="mini" :type="getLogStatusType(log.status)">{{ getLogStatusText(log.status) }}</el-tag>
              </div>
              <div class="log-action">{{ log.action }}</div>
              <div class="log-result">{{ log.response_data || log.result_summary || '-' }}</div>
            </div>
          </div>
        </el-card>
        
        <!-- 最近汇报 -->
        <el-card style="margin-top: 20px;">
          <h3>最近汇报</h3>
          <div v-if="!recentSummaries.length" class="empty-text">暂无汇报</div>
          <div v-else class="list-container">
            <div v-for="summary in recentSummaries" :key="summary.summary_id" class="summary-item">
              <div class="summary-header">
                <span class="summary-type">{{ getSummaryTypeText(summary.summary_type) }}</span>
                <span class="summary-date">{{ summary.summary_date }}</span>
              </div>
              <div class="summary-content">{{ summary.content || summary.summary_content || '-' }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>
    </BaseCard>
  </div>
</template>

<script>
import { getRobotDetail, getRobotAvatar } from '../api/robot'
import BaseCard from '@/components/BaseCard'
import WelcomeBanner from '@/components/WelcomeBanner'

export default {
  name: 'RobotDetail',
  components: { BaseCard, WelcomeBanner },
  data() {
    return {
      robotId: null,
      robot: {},
      stats: {
        total_tasks: 0,
        pending_tasks: 0,
        waiting_dependency_tasks: 0,
        processing_tasks: 0,
        completed_tasks: 0,
        failed_tasks: 0
      },
      recentTasks: [],
      recentSessions: [],
      recentLogs: [],
      recentSummaries: [],
      loading: false,
      avatarBase64: ''
    }
  },
  created() {
    this.robotId = this.$route.params.id
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        // 并行加载机器人详情和头像
        const [detailRes] = await Promise.all([
          getRobotDetail(this.robotId),
          this.loadAvatar()
        ])
        
        if (detailRes) {
          this.robot = detailRes.robot || detailRes
          this.stats = detailRes.stats || {
            total_tasks: 0,
            pending_tasks: 0,
            waiting_dependency_tasks: 0,
            processing_tasks: 0,
            completed_tasks: 0,
            failed_tasks: 0
          }
          this.recentTasks = detailRes.recent_tasks || []
          this.recentSessions = detailRes.recent_sessions || []
          this.recentLogs = detailRes.recent_logs || []
          this.recentSummaries = detailRes.recent_summaries || []
        }
      } catch (error) {
        console.error(error)
        this.$message.error('获取数据失败')
      } finally {
        this.loading = false
      }
    },
    async loadAvatar() {
      // 无论是否有头像字段，都尝试加载，因为后端会处理
      try {
        const res = await getRobotAvatar(this.robotId)
        if (res.avatar_base64) {
          this.avatarBase64 = res.avatar_base64
        }
      } catch (error) {
        // 404表示没有头像，这是正常的，不显示警告
        if (error.response && error.response.status !== 404) {
          console.warn('加载头像失败:', error)
        }
      }
    },
    getStatusClass(status) {
      const map = {
        0: 'status-offline',
        1: 'status-online',
        2: 'status-working',
        3: 'status-busy'
      }
      return map[status] || 'status-offline'
    },
    getStatusText(status) {
      const map = {
        0: '离线',
        1: '在线空闲',
        2: '工作中',
        3: '忙碌中'
      }
      return map[status] || '离线'
    },
    formatTime(time) {
      return new Date(time).toLocaleString('zh-CN')
    },
    
    // 获取头像渐变色
    getAvatarGradient(name) {
      if (!name) return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      const gradients = [
        'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
        'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
        'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
        'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)'
      ]
      let hash = 0
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash)
      }
      const index = Math.abs(hash) % gradients.length
      return gradients[index]
    },
    
    // 任务状态
    getTaskStatusType(status) {
      const map = { 0: 'info', 1: 'warning', 2: 'success', 3: 'danger', 4: 'info' }
      return map[status] || 'info'
    },
    getTaskStatusText(status) {
      const map = { 0: '待处理', 1: '进行中', 2: '已完成', 3: '已失败', 4: '已取消' }
      return map[status] || '未知'
    },
    
    // 会话状态
    getSessionStatusType(status) {
      const map = { 0: 'info', 1: 'success', 2: 'danger' }
      return map[status] || 'info'
    },
    getSessionStatusText(status) {
      const map = { 0: '进行中', 1: '已完成', 2: '已失败' }
      return map[status] || '未知'
    },
    
    // 日志状态
    getLogStatusType(status) {
      const map = { 1: 'warning', 2: 'success', 3: 'danger' }
      return map[status] || 'info'
    },
    getLogStatusText(status) {
      const map = { 1: '执行中', 2: '成功', 3: '失败' }
      return map[status] || '未知'
    },
    
    // 汇报类型
    getSummaryTypeText(type) {
      const map = { 'D': '日报', 'W': '周报', 'M': '月报' }
      return map[type] || type || '汇报'
    },
    
    // 跳转到任务详情
    goToTask(taskId) {
      this.$router.push({ name: 'TaskDetail', params: { id: taskId } })
    },
    
    // 返回上一页
    goBack() {
      this.$router.back()
    },
    
    // 编辑机器人
    handleEdit() {
      this.$router.push({ name: 'RobotEdit', params: { id: this.robotId } })
    },
    
    // 显示全屏头像
    showFullscreenAvatar() {
      const avatarUrl = this.avatarBase64 || ''
      if (!avatarUrl) {
        this.$message.info('暂无头像图片')
        return
      }
      
      // 创建全屏预览
      const fullscreenDiv = document.createElement('div')
      fullscreenDiv.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(0, 0, 0, 0.9);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
        cursor: zoom-out;
      `
      
      const img = document.createElement('img')
      img.src = avatarUrl
      img.style.cssText = `
        max-width: 90%;
        max-height: 90%;
        object-fit: contain;
        border-radius: 8px;
      `
      
      fullscreenDiv.appendChild(img)
      document.body.appendChild(fullscreenDiv)
      
      // 点击关闭
      fullscreenDiv.addEventListener('click', () => {
        document.body.removeChild(fullscreenDiv)
      })
      
      // ESC键关闭
      const handleEsc = (e) => {
        if (e.key === 'Escape') {
          if (fullscreenDiv.parentNode) {
            document.body.removeChild(fullscreenDiv)
          }
          document.removeEventListener('keydown', handleEsc)
        }
      }
      document.addEventListener('keydown', handleEsc)
    }
  }
}
</script>

<style lang="scss" scoped>
.container {
  padding: 20px;
}

/* 页面头部导航 */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 16px 20px;
  background-color: var(--bg-card);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-actions {
  display: flex;
  gap: 8px;
}

/* 信息卡片 */
.info-card {
  .avatar-wrapper {
    text-align: center;
    padding: 20px 0;
  }

  .avatar {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    object-fit: cover;
    transition: all 0.3s;
  }

  .avatar-placeholder {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 48px;
    color: var(--text-white);
    margin: 0 auto;
    transition: all 0.3s;
  }

  h2 {
    margin-top: 16px;
    margin-bottom: 8px;
  }
}

.avatar-container {
  position: relative;
  display: inline-block;
  cursor: pointer;

  &:hover {
    .avatar-overlay {
      opacity: 1;
    }

    .avatar,
    .avatar-placeholder {
      filter: brightness(0.8);
    }
  }
}

.avatar-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;

  i {
    color: var(--text-white);
    font-size: 24px;
  }
}

.info-item {
  display: flex;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-light);

  .label {
    font-weight: 600;
    width: 80px;
    color: var(--text-secondary);
  }
}

.status-wrapper {
  display: flex;
  align-items: center;
  padding: 12px 0;
  margin-top: 8px;
}

.status-badge {
  padding: 4px 12px;
  border-radius: var(--radius-sm);
  font-size: 14px;
  font-weight: 500;
}

.status-offline {
  background-color: var(--status-offline);
  color: var(--text-white);
}

.status-online {
  background-color: var(--status-online);
  color: var(--text-white);
}

.status-working {
  background-color: var(--status-working);
  color: var(--text-white);
}

.status-busy {
  background-color: var(--status-busy);
  color: var(--text-white);
}

/* 统计卡片 */
.stats-card {
  h3 {
    margin-top: 0;
    margin-bottom: 16px;
  }
}

.stat-item {
  text-align: center;
  padding: 12px 0;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: var(--primary-color);
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12px;
  color: var(--text-muted);
}

/* 空状态 */
.empty-text {
  text-align: center;
  color: var(--text-muted);
  padding: 40px 0;
}

/* 列表容器 */
.list-container {
  max-height: 300px;
  overflow-y: auto;
}

/* 任务项 */
.task-item {
  padding: 12px;
  border-bottom: 1px solid var(--border-light);
  cursor: pointer;
  transition: background-color 0.2s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: var(--bg-hover);
  }
}

.task-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.task-title {
  font-weight: 500;
  color: var(--text-primary);
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 8px;
}

.task-meta {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: var(--text-muted);
}

.task-priority {
  color: var(--warning-color);

  &.high {
    color: var(--danger-color);
  }

  &.low {
    color: var(--success-color);
  }
}

/* 会话项 */
.session-item {
  padding: 12px;
  border-bottom: 1px solid var(--border-light);

  &:last-child {
    border-bottom: none;
  }
}

.session-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.session-title {
  font-weight: 500;
  color: var(--text-primary);
}

.session-meta {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: var(--text-muted);
}

.session-count {
  color: var(--primary-color);
}

/* 日志项 */
.log-item {
  padding: 12px;
  border-bottom: 1px solid var(--border-light);

  &:last-child {
    border-bottom: none;
  }
}

.log-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.log-time {
  font-size: 12px;
  color: var(--text-muted);
}

.log-action {
  font-weight: 500;
  margin-bottom: 4px;
  color: var(--text-primary);
}

.log-result {
  color: var(--text-secondary);
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 汇报项 */
.summary-item {
  padding: 12px;
  border-bottom: 1px solid var(--border-light);

  &:last-child {
    border-bottom: none;
  }
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.summary-type {
  font-weight: 500;
  color: var(--primary-color);
}

.summary-date {
  font-size: 12px;
  color: var(--text-muted);
}

.summary-content {
  color: var(--text-secondary);
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.5;
}
</style>
